import { useEffect, useState } from "react";
import { Plus, Save, X, Trash2, CreditCard, Smartphone, Wallet, Receipt, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTime, fmtMoney } from "@/lib/format";

const METHOD_OPTIONS = [
  { value: "zelle", label_es: "Zelle", label_en: "Zelle", icon: Smartphone },
  { value: "cash", label_es: "Efectivo", label_en: "Cash", icon: Wallet },
  { value: "check", label_es: "Cheque", label_en: "Check", icon: Receipt },
  { value: "other", label_es: "Otro", label_en: "Other", icon: Receipt },
];

const methodMeta = (method, lang) => {
  const m = METHOD_OPTIONS.find((x) => x.value === method);
  if (!m) return { label: method, Icon: Receipt };
  return { label: lang === "es" ? m.label_es : m.label_en, Icon: m.icon };
};

const emptyManual = {
  amount: "",
  method: "zelle",
  donor_name: "",
  donor_phone: "",
  note: "",
  received_at: new Date().toISOString().slice(0, 10),
  reference: "",
};

export default function AdminDonations() {
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(emptyManual);
  const [saving, setSaving] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingOne, setSyncingOne] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/donations");
      setList(data);
    } catch (_) {
      // network error — silently ignore; user can re-trigger via sync button
    }
  };
  useEffect(() => { reload(); }, []);

  const filtered = list.filter((d) => {
    if (!q) return true;
    const lq = q.toLowerCase();
    return (
      (d.donor_name || "").toLowerCase().includes(lq)
      || (d.note || "").toLowerCase().includes(lq)
      || (d.session_id || "").includes(q)
      || (d.method || "").toLowerCase().includes(lq)
    );
  });
  const totalPaid = filtered
    .filter((d) => d.payment_status === "paid")
    .reduce((a, b) => a + Number(b.amount || 0), 0);

  const onSaveManual = async () => {
    if (!draft.amount || Number(draft.amount) <= 0) {
      toast.error(lang === "es" ? "Ingresa un monto valido" : "Enter a valid amount");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/offerings/manual", { ...draft, amount: Number(draft.amount) });
      toast.success(t.admin.saved);
      setOpen(false);
      setDraft({ ...emptyManual, received_at: new Date().toISOString().slice(0, 10) });
      reload();
    } catch (_) {
      toast.error(t.common.error);
    } finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/offerings/${deleteTarget.id}`);
      toast.success(
        data?.tombstoned
          ? (lang === "es"
              ? "Eliminado de la aplicación (sigue en Stripe)"
              : "Removed from app (still kept in Stripe)")
          : (t.admin.deleted || (lang === "es" ? "Eliminado" : "Deleted"))
      );
      setDeleteTarget(null);
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail || t.common.error;
      toast.error(String(detail));
    } finally {
      setDeleting(false);
    }
  };

  const onSyncAll = async () => {
    setSyncingAll(true);
    try {
      const { data } = await api.post("/admin/payments/reconcile?include_stripe_history=true");
      const s = data?.summary || {};
      const parts = [];
      if (s.updated_in_db) parts.push(lang === "es" ? `${s.updated_in_db} actualizadas` : `${s.updated_in_db} updated`);
      if (s.imported_from_stripe) parts.push(lang === "es" ? `${s.imported_from_stripe} importadas` : `${s.imported_from_stripe} imported`);
      if (s.now_paid) parts.push(lang === "es" ? `${s.now_paid} pagadas` : `${s.now_paid} paid`);
      if (s.now_expired) parts.push(lang === "es" ? `${s.now_expired} expiradas` : `${s.now_expired} expired`);
      if (parts.length === 0) parts.push(lang === "es" ? "Todo al dia" : "Everything up to date");
      toast.success(parts.join(" · "));
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail || t.common.error;
      toast.error(String(detail));
    } finally {
      setSyncingAll(false);
    }
  };

  const onSyncOne = async (sessionId) => {
    if (!sessionId || !sessionId.startsWith("cs_")) return;
    setSyncingOne(sessionId);
    try {
      const { data } = await api.post(`/admin/payments/${sessionId}/reconcile`);
      const tx = data?.transaction;
      if (tx) {
        const status = tx.payment_status;
        toast.success(lang === "es" ? `Estado: ${status}` : `Status: ${status}`);
      } else {
        toast.success(lang === "es" ? "Sincronizado" : "Synced");
      }
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail || t.common.error;
      toast.error(String(detail));
    } finally {
      setSyncingOne(null);
    }
  };

  const sourceBadge = (d) => {
    if (d.manual) {
      const { Icon, label } = methodMeta(d.method, lang);
      return (
        <Badge variant="secondary" className="rounded-full gap-1">
          <Icon className="h-3 w-3" />{label}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="rounded-full gap-1">
        <CreditCard className="h-3 w-3" />Stripe
      </Badge>
    );
  };

  const statusVariant = (s) => {
    if (s === "paid" || s === "complete" || s === "completed" || s === "succeeded") return "default";
    if (s === "expired") return "destructive";
    if (s === "open" || s === "pending" || s === "initiated") return "secondary";
    return "outline";
  };

  const statusLabel = (s) => {
    if (lang !== "es") return s || "";
    const map = {
      paid: "pagado",
      complete: "completo",
      completed: "completo",
      succeeded: "pagado",
      expired: "expirada",
      open: "en proceso",
      pending: "pendiente",
      initiated: "iniciada",
      unpaid: "no pagada",
    };
    return map[s] || s || "";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-serif-display text-2xl font-semibold">{t.admin.donations}</h1>
          <p className="text-sm text-muted-foreground">Total recibido: <strong className="text-foreground">{fmtMoney(totalPaid)}</strong></p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={onSyncAll}
            disabled={syncingAll}
            data-testid="admin-donations-sync-all"
            title={lang === "es"
              ? "Actualiza el estado de todas las donaciones de Stripe e importa cualquiera que falte"
              : "Refresh status of all Stripe donations and import any missing ones"}
          >
            <RefreshCw className={`mr-1 h-4 w-4 ${syncingAll ? "animate-spin" : ""}`} />
            {syncingAll
              ? (lang === "es" ? "Sincronizando..." : "Syncing...")
              : (lang === "es" ? "Sincronizar con Stripe" : "Sync with Stripe")}
          </Button>
          <Button onClick={() => { setDraft({ ...emptyManual, received_at: new Date().toISOString().slice(0, 10) }); setOpen(true); }} data-testid="admin-donations-add-manual">
            <Plus className="mr-1 h-4 w-4" />
            {lang === "es" ? "Registrar ofrenda manual" : "Log manual offering"}
          </Button>
        </div>
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "es" ? "Buscar..." : "Search..."} className="h-11" data-testid="admin-donations-search" />

      <Card className="rounded-2xl ring-1 ring-black/5 overflow-hidden">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground" data-testid="admin-donations-empty">{t.admin.noItems}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="admin-donations-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t.give.donorName}</TableHead>
                    <TableHead>{lang === "es" ? "Metodo" : "Method"}</TableHead>
                    <TableHead>{lang === "es" ? "Monto" : "Amount"}</TableHead>
                    <TableHead>{lang === "es" ? "Estado" : "Status"}</TableHead>
                    <TableHead>{lang === "es" ? "Nota" : "Note"}</TableHead>
                    <TableHead>{lang === "es" ? "Fecha" : "Date"}</TableHead>
                    <TableHead className="text-right">&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((d) => {
                    const isStripe = !d.manual && (d.session_id || "").startsWith("cs_");
                    const canSync = isStripe && d.payment_status !== "paid";
                    const syncing = syncingOne === d.session_id;
                    return (
                      <TableRow key={d.id || d.session_id}>
                        <TableCell className="font-medium">{d.donor_name || "\u2014"}</TableCell>
                        <TableCell>{sourceBadge(d)}</TableCell>
                        <TableCell>{fmtMoney(d.amount || 0, d.currency || "usd")}</TableCell>
                        <TableCell><Badge variant={statusVariant(d.payment_status)}>{statusLabel(d.payment_status)}</Badge></TableCell>
                        <TableCell className="max-w-[240px] truncate">{d.note || (d.reference ? `Ref: ${d.reference}` : "\u2014")}</TableCell>
                        <TableCell>{fmtDateTime(d.received_at || d.created_at, lang)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canSync && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => onSyncOne(d.session_id)}
                                disabled={syncing}
                                aria-label="sync"
                                title={lang === "es" ? "Sincronizar con Stripe" : "Sync with Stripe"}
                                data-testid={`sync-stripe-${d.session_id}`}
                              >
                                <RefreshCw className={`h-4 w-4 text-muted-foreground hover:text-foreground ${syncing ? "animate-spin" : ""}`} />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteTarget(d)}
                              aria-label="delete"
                              title={lang === "es" ? "Eliminar de la aplicación" : "Delete from app"}
                              data-testid={d.manual ? `delete-manual-${d.id}` : `delete-stripe-${d.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="manual-offering-dialog">
          <DialogHeader>
            <DialogTitle>{lang === "es" ? "Registrar ofrenda manual" : "Log manual offering"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">{lang === "es" ? "Metodo" : "Method"}</Label>
              <Select value={draft.method} onValueChange={(v) => setDraft({ ...draft, method: v })}>
                <SelectTrigger data-testid="manual-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METHOD_OPTIONS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{lang === "es" ? m.label_es : m.label_en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{lang === "es" ? "Monto" : "Amount"} (USD)</Label>
                <Input type="number" step="0.01" min="0" inputMode="decimal" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} data-testid="manual-amount" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{lang === "es" ? "Fecha recibida" : "Date received"}</Label>
                <Input type="date" value={draft.received_at} onChange={(e) => setDraft({ ...draft, received_at: e.target.value })} data-testid="manual-date" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">{t.give.donorName}</Label>
              <Input value={draft.donor_name} onChange={(e) => setDraft({ ...draft, donor_name: e.target.value })} data-testid="manual-donor-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{t.register.phone}</Label>
                <Input value={draft.donor_phone} onChange={(e) => setDraft({ ...draft, donor_phone: e.target.value })} data-testid="manual-donor-phone" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{lang === "es" ? "Referencia" : "Reference"}</Label>
                <Input value={draft.reference} onChange={(e) => setDraft({ ...draft, reference: e.target.value })} placeholder={lang === "es" ? "# cheque, confirmacion..." : "Check #, confirmation..."} data-testid="manual-reference" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">{t.give.noteLabel}</Label>
              <Textarea rows={2} value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} data-testid="manual-note" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}><X className="mr-1 h-4 w-4" />{t.common.cancel}</Button>
            <Button onClick={onSaveManual} disabled={saving} data-testid="manual-save"><Save className="mr-1 h-4 w-4" />{saving ? "..." : t.common.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-donation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {lang === "es" ? "¿Eliminar esta ofrenda?" : "Delete this donation?"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                {deleteTarget && (
                  <div className="rounded-lg bg-muted/50 p-3 ring-1 ring-black/5">
                    <div className="font-medium text-foreground">
                      {deleteTarget.donor_name || (lang === "es" ? "Sin nombre" : "No name")} · {fmtMoney(deleteTarget.amount || 0, deleteTarget.currency || "usd")}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {fmtDateTime(deleteTarget.received_at || deleteTarget.created_at, lang)}
                    </div>
                  </div>
                )}
                {deleteTarget && !deleteTarget.manual && (deleteTarget.session_id || "").startsWith("cs_") ? (
                  <p>
                    {lang === "es"
                      ? "Esta acción solo elimina el registro de la aplicación. La transacción seguirá existiendo en tu cuenta de Stripe y no se borrará al sincronizar."
                      : "This only removes the record from the app. The transaction will still exist in your Stripe account and won't reappear when you sync."}
                  </p>
                ) : (
                  <p>
                    {lang === "es"
                      ? "Esta acción no se puede deshacer."
                      : "This action cannot be undone."}
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-donation-cancel">{t.common.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-donation-confirm"
            >
              <Trash2 className="mr-1 h-4 w-4" />
              {deleting
                ? (lang === "es" ? "Eliminando..." : "Deleting...")
                : (lang === "es" ? "Eliminar de la app" : "Delete from app")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
